import { useCallback } from 'react';

import { UseRouteAuthProps, AuthValidationResult } from './types';
import { validateAuthentication } from './validateAuthentication';
import { validateClientAccess } from './validateClientAccess';
import { validateUserRole } from './validateUserRole';

export const useRouteAuth = ({ pathname, matchPath: path, user, clients, roles }: UseRouteAuthProps) => {
  const validateAuth = useCallback((): AuthValidationResult => {
    // Validate client access
    const clientValidation = validateClientAccess(pathname, path, clients);
    const { client, isValid: hasValidClient, clientCodeExists } = clientValidation;

    // Check authentication
    const isAuthenticated = validateAuthentication(user, client);

    if (!isAuthenticated) {
      return {
        isAuthenticated: false,
        hasValidClient: false,
        hasRequiredRole: false,
        error: 'Authentication required',
      };
    }

    // Validate client if code exists
    if (clientCodeExists && !hasValidClient) {
      return {
        isAuthenticated: true,
        hasValidClient: false,
        hasRequiredRole: false,
        error: 'Client not found',
      };
    }

    // Check role permissions
    const hasRequiredRole = validateUserRole(user, roles);

    return {
      isAuthenticated: true,
      hasValidClient: true,
      hasRequiredRole,
      client,
      error: !hasRequiredRole ? 'Insufficient permissions' : undefined,
    };
  }, [pathname, path, user, clients, roles]);

  return validateAuth;
};
